import {
  Accordion,
  Badge,
  Breadcrumb,
  Button,
  Card,
  ComponentType,
  Grid,
  Heading,
  HeadingType,
  Icon,
  IconName,
  Loader,
  breakPoints,
  useMediaQuery,
} from '@gcl/react-core';
import { DisplayWrapper } from '@core/DisplayWrapper/DisplayWrapper';
import { PageLayout } from '@app/PageLayout';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { useHasPermission } from '@config/config.hooks';
import { EPermission } from '@userProfile/permission/permission.constants';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useGetMyGems } from './myGems.queries';
import './MyGemsPage.scss';
import { useMyGemsEditModal } from './myGemsEditModal.hooks';
import { IMyGems } from './myGems.models';

export const MyGemsPage = () => {
  const { navigate } = useNavigate();

  const [mode, setMode] = React.useState<DetailPageAccessMode>('View');
  const [selectedMyGems, setSelectedMyGems] = React.useState<IMyGems | undefined>(undefined);
  const [selectedParent, setSelectedParent] = React.useState<IMyGems | undefined>(undefined);

  const isSm = useMediaQuery(breakPoints.breakpointSm);
  const isXs = useMediaQuery(breakPoints.breakpointXs);
  const isMobile = isSm || isXs;

  const isPermittedEdit = useHasPermission(EPermission.MYGEMS_EDIT);

  const {
    data: dataMyGems,
    isLoading: isLoadingMyGems,
    isRefetching: isRefetchingMyGems,
    error: errorMyGems,
  } = useGetMyGems({ shouldIncludeInactive: mode === 'Update' });

  const { renderModal: renderMyGemsEditModal, openPortal: openMyGemsEditModal } = useMyGemsEditModal({
    myGems: selectedMyGems,
    parent: selectedParent,
  });

  const onClickEditMyGems = (
    selected: IMyGems | undefined,
    parent: IMyGems | undefined,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<SVGElement, MouseEvent>,
  ) => {
    setSelectedMyGems(selected);
    setSelectedParent(parent);
    openMyGemsEditModal(e);
  };
  return (
    <PageLayout
      isGlobalPage
      renderMainContent={({ isMenuHidden }) => (
        <div className="gcl-px-md gcl-mx-xl gcl-my-md">
          <ApiErrorDisplay errors={[{ error: errorMyGems, genericError: 'Get my gems list failed' }]} />
          <div className="gcl-pb-m gcl-gems-mygems-breadcrumb-container">
            <Breadcrumb
              items={[
                {
                  text: 'Home',
                  prefixIconName: 'House' as IconName,
                  onClick: () => {
                    navigate(routes.home.path);
                  },
                },
                {
                  text: `MyGems`,
                  prefixIconName: 'JournalText' as IconName,
                  active: true,
                },
              ]}
            />
            {dataMyGems && isRefetchingMyGems && (
              <Loader className="gcl-ml-md" small text="Fetching latest data from server" />
            )}
          </div>
          <Heading as={HeadingType.h1} styledAs={HeadingType.h2} className="gcl-pb-lg">
            My GEMS
          </Heading>
          <DisplayWrapper
            isLoading={isLoadingMyGems}
            loaderProps={[
              { suffixBlankRow: true, numberOfRows: 1, lineHeight: 20, lineWidth: 20 },
              { suffixBlankRow: true, numberOfRows: 1, lineHeight: 20, lineWidth: 350 },
              { suffixBlankRow: true, numberOfRows: 1, lineHeight: 20, lineWidth: 300 },
              { suffixBlankRow: true, numberOfRows: 1, lineHeight: 20, lineWidth: 320 },
            ]}
          >
            {mode === 'Update' && (
              <div className=" gcl-gems-mygems-top-bar-edit">
                <Button type={ComponentType.Primary} onClick={(e) => onClickEditMyGems(undefined, undefined, e)}>
                  Add Section
                </Button>

                <Button type={ComponentType.Danger} onClick={() => setMode('View')}>
                  Cancel
                </Button>
              </div>
            )}
            {mode === 'View' && isPermittedEdit && (
              <div className="gcl-gems-mygems-top-bar-view">
                <Icon iconName="Pencil" onClick={() => setMode('Update')} />
              </div>
            )}

            {dataMyGems?.map((section) => {
              const accordions = section.children?.map((accordion) => {
                if ((accordion.children?.length ?? 0) <= 0) {
                  return (
                    <div
                      key={accordion.id}
                      className={`${accordion.itemUrl ? 'gcl-clickable' : ''} gcl-gems-mygems-subheader gcl-mb-md`}
                      onClick={() => {
                        if (accordion.itemUrl) window.open(accordion.itemUrl);
                      }}
                    >
                      <span className="gcl-gems-mygems-title">
                        <span className="gcl-ft-lg">{accordion.itemName}</span>
                        {mode === 'Update' && <span className="gcl-gems-mygems-seq"> [Seq: {accordion.itemSeq}]</span>}
                      </span>
                      {accordion.isNew && <div className="gcl-gems-mygems-new">NEW</div>}
                      {mode === 'Update' && (
                        <>
                          {!accordion.itemActive && (
                            <Badge type={ComponentType.Danger} pills>
                              Inactive
                            </Badge>
                          )}
                          <Icon
                            iconName="Pencil"
                            className="gcl-ml-md"
                            onClick={(e) => onClickEditMyGems(accordion, section, e)}
                          />
                          <Icon
                            iconName="PlusLg"
                            className="gcl-ml-md"
                            onClick={(e) => {
                              onClickEditMyGems(undefined, accordion, e);
                            }}
                          />
                        </>
                      )}
                    </div>
                  );
                }
                return (
                  <Accordion
                    allowClickHeaderToExpand
                    className="gcl-mb-md"
                    key={accordion.id}
                    content={{
                      id: accordion.id,
                      onRenderHeader: () => (
                        <div
                          className="gcl-gems-mygems-children-container gcl-gems-mygems-edit-container  gcl-my-sm"
                          key={accordion.id}
                        >
                          <div className="gcl-gems-mygems-title gcl-ft-lg">
                            {accordion.itemName}
                            {mode === 'Update' && (
                              <span className="gcl-gems-mygems-seq"> [Seq: {accordion.itemSeq}]</span>
                            )}
                          </div>
                        </div>
                      ),
                      onRenderHeaderButtons: () => (
                        <>
                          {accordion.isNew && <div className="gcl-gems-mygems-new">NEW</div>}
                          {mode === 'Update' && (
                            <>
                              {!accordion.itemActive && (
                                <Badge type={ComponentType.Danger} pills>
                                  Inactive
                                </Badge>
                              )}
                              <Icon iconName="Pencil" onClick={(e) => onClickEditMyGems(accordion, section, e)} />
                              <Icon
                                iconName="PlusLg"
                                onClick={(e) => {
                                  onClickEditMyGems(undefined, accordion, e);
                                }}
                              />
                            </>
                          )}
                        </>
                      ),
                      onRenderBody: () => (
                        <>
                          {accordion.children?.map((child) => {
                            return (
                              <div className="gcl-gems-mygems-children-container gcl-my-sm" key={child.id}>
                                <div className="gcl-gems-mygems-title">
                                  <div
                                    className={`${child.itemUrl ? ' gcl-gems-mygems-clickable ' : ''} `}
                                    onClick={() => {
                                      if (child.itemUrl) window.open(child.itemUrl);
                                    }}
                                  >
                                    {child.itemName}
                                    {mode === 'Update' && (
                                      <span className="gcl-gems-mygems-seq"> [Seq: {child.itemSeq}]</span>
                                    )}
                                  </div>
                                </div>
                                {child.isNew && <div className="gcl-gems-mygems-new">NEW</div>}
                                {mode === 'Update' && (
                                  <>
                                    {!child.itemActive && (
                                      <Badge type={ComponentType.Danger} pills>
                                        Inactive
                                      </Badge>
                                    )}
                                    <Icon
                                      iconName="Pencil"
                                      className="gcl-ml-md"
                                      onClick={(e) => onClickEditMyGems(child, accordion, e)}
                                    />
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </>
                      ),
                    }}
                  />
                );
              });
              let cardContent = null;

              if (isMobile) {
                cardContent = (
                  <Grid container>
                    {accordions?.map((accordion, index) => {
                      return (
                        <Grid item xs={12} key={accordion.key}>
                          {accordion}
                        </Grid>
                      );
                    })}
                  </Grid>
                );
              } else {
                const oddAccordions = accordions?.filter((v, index) => index % 2 === 1);
                const evenAccordions = accordions?.filter((v, index) => index % 2 === 0);

                cardContent = (
                  <Grid container spacing={2}>
                    <Grid item md={6} sm={12}>
                      {evenAccordions}
                    </Grid>
                    <Grid item md={6} sm={12}>
                      {oddAccordions}
                    </Grid>
                  </Grid>
                );
              }

              return (
                <Card
                  key={section.id}
                  className="gcl-gems-mygems-section"
                  title={
                    <div className="gcl-gems-mygems-edit-container ">
                      <div className="gcl-gems-mygems-title">
                        {section.itemName}
                        {mode === 'Update' && <span className="gcl-gems-mygems-seq"> [Seq: {section.itemSeq}]</span>}
                      </div>
                      {section.isNew && <div className="gcl-gems-mygems-new">NEW</div>}

                      {mode === 'Update' && (
                        <>
                          {!section.itemActive && (
                            <Badge type={ComponentType.Danger} pills className="gcl-ml-md">
                              Inactive
                            </Badge>
                          )}
                          <Icon
                            iconName="Pencil"
                            className="gcl-ml-md"
                            onClick={(e) => onClickEditMyGems(section, undefined, e)}
                          />
                          <Icon
                            iconName="PlusLg"
                            className="gcl-ml-md"
                            onClick={(e) => {
                              onClickEditMyGems(undefined, section, e);
                            }}
                          />
                        </>
                      )}
                    </div>
                  }
                  content={cardContent}
                />
              );
            })}
          </DisplayWrapper>
          {renderMyGemsEditModal({})}
        </div>
      )}
    />
  );
};
